import 'ag-grid-enterprise';  
import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ColDef,
  ValueGetterParams,
  GridReadyEvent,
  CellValueChangedEvent,
  GridApi
} from 'ag-grid-community';

import { FreightService } from './freight.service';
import { AuthService } from '../../auth.service';
import {
  FreightProduct,
  FreightHeader,
  FreightSerialMatch,
  LadingPackage,
  TgRequirement,
  Package17Extras,
  PackageBase,
  ConfirmationReview
} from './freight.models';
import { ProductLookupDialog } from './product-lookup-dialog';
import { FreightSerialSelectDialogComponent }
  from './freight-serial-select-dialog/freight-serial-select-dialog.component';

import { LADING_TYPE_CONFIG, LadingTypeConfig } from './freight-config';
import { LadingType } from './types/lading-type.enum';

import { ConfirmDialogComponent, ConfirmDialogData } from '../../confirm-dialog/confirm-dialog.component';

/** Type-guard: is this a type-17 package? */
function isType17(
  pkg: LadingPackage
): pkg is (PackageBase & Package17Extras & { products: FreightProduct[] }) {
  return (pkg as any).packaging_type !== undefined;
}

@Component({
  selector: 'app-freight',
  templateUrl: './freight.component.html',
  styleUrls: ['./freight.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FreightComponent implements OnInit {
  /** expose enum to template */
  LadingType = LadingType;

  /* ---------------- state ---------------- */
  ladingUuid = '';
  header!: FreightHeader;
  packages: LadingPackage[] = [];
  requirements: TgRequirement[] = [];
  isConsigneeWarehouse: boolean = true;

  /* derived */
  outstandingReqs: TgRequirement[] = [];
  hasDiscrepancy = false;
  storeNumber: string | null = null;   /* type-17 header */
  allDelivered = false;

  loading = true;
  @ViewChild('confirmationReviewDialog') confirmationReviewTemplate!: TemplateRef<unknown>;
  confirmationReview: ConfirmationReview | null = null;
  confirmationReviewLoading = false;
  confirmationUploading = false;
  confirmationNote = '';
  confirmationError = '';
  confirmationMessage = '';
  private confirmationDialog?: MatDialogRef<unknown>;


  /* ag-Grid */
  masterColumnDefs: ColDef[] = [];
  detailCellRendererParams: any = {};
  private detailGridApi?: GridApi;
  private readonly storageKey = 'freight-detail-grid-state';
  /** grid height to fill remaining viewport */
  public gridHeight = 'calc(100vh - 250px)'; 

  private typeConfig!: LadingTypeConfig;
  private actualCounts = new Map<string, number>();

  constructor(
    private freightSvc: FreightService,
    private auth: AuthService,
    private dlg: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.ladingUuid = this.route.snapshot.paramMap.get('uuid') ?? '';
    if (!this.ladingUuid) { return; }
    this.reload();

    // Determine if QR codes should show
    // const warehouse_url = `/api/work_orders/freight/is_consignee_a_warehouse.php?lading_uuid=${this.ladingUuid}`;
    // this.http.get<{ result: boolean }>(warehouse_url).subscribe(res => {
    //   this.isConsigneeWarehouse = res['result'];
    //   console.log(res)
    // })
  }

  private reload(): void {
    this.loading = true;
    this.freightSvc.load(this.ladingUuid).subscribe({
      next: r => {
        console.log(r.packages)
        this.header       = r.header;
        this.packages     = r.packages;
        this.requirements = r.requirements;

        const lt = this.header.lading_type as LadingType;
        this.typeConfig   = LADING_TYPE_CONFIG[lt];

        this.rebuildCounts();
        this.prepareType17Header(lt);
        this.setupGrid(lt);

        this.loading = false;
        this.cdr.markForCheck();
      },
      error: err => {
        console.error(err);
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  get canReviewConfirmation(): boolean {
    return this.auth.hasRole('SUPER_ADMIN') && this.header?.lading_type === LadingType.LT16
      && this.requirements.length > 0;
  }

  get confirmationLineChanges() {
    return this.confirmationReview?.substitutions.filter(row => row.stored_line !== row.line) ?? [];
  }

  openConfirmationReview(): void {
    if (!this.canReviewConfirmation || this.confirmationReviewLoading) { return; }
    this.confirmationReviewLoading = true;
    this.confirmationMessage = '';
    this.confirmationError = '';
    this.freightSvc.reviewConfirmation(this.ladingUuid).subscribe({
      next: review => {
        this.confirmationReviewLoading = false;
        if (!review.success) {
          this.confirmationMessage = review.message || 'Could not review this confirmation.';
        } else {
          this.confirmationReview = review;
          this.confirmationNote = '';
          this.confirmationDialog = this.dlg.open(this.confirmationReviewTemplate, {
            width: '900px', maxWidth: '95vw', maxHeight: '90vh', autoFocus: false
          });
        }
        this.cdr.markForCheck();
      },
      error: error => {
        this.confirmationReviewLoading = false;
        this.confirmationMessage = error.error?.message || 'Could not review this confirmation.';
        this.cdr.markForCheck();
      }
    });
  }

  closeConfirmationReview(): void {
    if (!this.confirmationUploading) { this.confirmationDialog?.close(); }
  }

  approveConfirmation(): void {
    const review = this.confirmationReview;
    if (!this.canReviewConfirmation || this.header?.delivery_confirmation || !review?.can_upload || !this.confirmationNote.trim() || this.confirmationUploading) { return; }
    this.confirmationUploading = true;
    this.confirmationError = '';
    if (this.confirmationDialog) { this.confirmationDialog.disableClose = true; }
    this.freightSvc.approveConfirmation(review, this.confirmationNote).subscribe({
      next: result => {
        this.confirmationUploading = false;
        if (this.confirmationDialog) { this.confirmationDialog.disableClose = false; }
        if (result.success) {
          this.confirmationDialog?.close();
          this.confirmationMessage = result.message;
          this.reload();
        } else {
          this.confirmationError = result.message || 'The upload did not complete.';
        }
        this.cdr.markForCheck();
      },
      error: error => {
        this.confirmationUploading = false;
        if (this.confirmationDialog) { this.confirmationDialog.disableClose = false; }
        this.confirmationError = error.error?.message || 'The upload did not complete.';
        this.cdr.markForCheck();
      }
    });
  }

  private rebuildCounts(): void {
    this.actualCounts.clear();
    this.packages.forEach(pkg =>
      pkg.products.forEach(pr => {
        if (!pr.product_code) { return; }
        const code = pr.product_code.trim().toUpperCase();
        this.actualCounts.set(code, (this.actualCounts.get(code) || 0) + 1);
      })
    );
    this.outstandingReqs = this.requirements.filter(r => !this.isFulfilled(r));
    this.hasDiscrepancy  = this.outstandingReqs.length > 0;
  }

  isFulfilled(req: TgRequirement): boolean {
    const code = req.item_code.trim().toUpperCase();
    return (this.actualCounts.get(code) || 0) >= req.quantity;
  }

  private prepareType17Header(lt: LadingType): void {
    if (lt !== LadingType.LT17) {
      this.storeNumber  = null;
      this.allDelivered = false;
      return;
    }
    const stores = new Set<string>();
    this.packages.filter(isType17).forEach(pkg =>
      pkg.products.forEach(pr => { if (pr.store_number) stores.add(pr.store_number); })
    );
    this.storeNumber  = stores.size === 1 ? [...stores][0] : null;
    this.allDelivered = this.packages
      .filter(isType17)
      .every(pkg => pkg.products.every(pr => pr.delivered === true));
  }

  addPackage(): void {
    (document.activeElement as HTMLElement | null)?.blur();
    const ref = this.dlg.open(ProductLookupDialog, { width: '450px' });
    ref.afterClosed().subscribe(sel => {
      const packageId = sel?.package_uuid;
      const productId = sel?.product_uuid;
      if (!packageId || !productId) { return; }
      this.freightSvc.attach(this.ladingUuid, packageId, productId)
        .subscribe(() => this.reload());
    });
  }

  removePackage(pkg: LadingPackage): void {
    this.freightSvc.detach(pkg.lading_package_uuid).subscribe(() => this.reload());
  }

  openPackageLabels(): void {
    if (!this.ladingUuid || !this.packages.length) { return; }
    this.router.navigate(['/work_order_package_labels', this.ladingUuid]);
  }

  onAddProductForRequirement(req: TgRequirement): void {
    this.freightSvc.search(req.item_code).subscribe(resp => {
      console.log(resp);

      if (!resp.serialized) {
        if (resp.matches && resp.matches.length) {
          // non-serialized + matches → just attach
          const match = resp.matches[0];
          this.freightSvc.attach(
            this.ladingUuid,
            match.package_uuid,
            match.product_uuid
          )
            .subscribe(() => this.reload());
        } else {
          // non-serialized + no matches 
           this.dlg.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
              width: '420px',
              data: {
                title: 'No Matches Found',
                message: `No products available for item code "${req.item_code}".`
                // Optional:
                // confirmText: 'Open Lookup',
                // cancelText: 'Close'
              }
            })
          }
          return;
        }

      // serialized → open the serial picker
      (document.activeElement as HTMLElement | null)?.blur();
      const dlg = this.dlg.open(FreightSerialSelectDialogComponent, {
        width: '400px',
        data: { matches: resp.matches || [], maxSelectable: req.quantity }
      });
      dlg.afterClosed().subscribe((sel: FreightSerialMatch[] | undefined) => {
        sel?.forEach(m =>
          this.freightSvc.attach(this.ladingUuid, m.package_uuid, m.product_uuid)
            .subscribe(() => this.reload())
        );
      });
    });
  }

  onAddProduct(_pkg: LadingPackage): void {}
  onEditProduct(_p: FreightProduct, _pkg: LadingPackage): void {}
  onDeleteProduct(_p: FreightProduct, _pkg: LadingPackage): void {}

  /** from freight-config, whether multi-product packaging is allowed */
  get allowMultiProductPackaging(): boolean {
  // Add a "guard clause" to check if typeConfig has been initialized.
  if (!this.typeConfig) {
    return false; // Return a safe default value
  }
  const v = this.typeConfig.multiProductPackaging;
  return typeof v === 'function' ? v(this.header) : !!v;
}

  // onQuickFilterChanged(value: string): void {
  //   this.detailGridApi?.setQuickFilter(value);
  // }

  private labels(w?: number, m?: number) {
    return { w: w === 2 ? 'kg' : 'lb', d: m === 2 ? 'cm' : 'in' };
  }

  private setupGrid(lt: LadingType): void {
    if (lt !== LadingType.LT17) { return; }

    // Master grid columns
    this.masterColumnDefs = [
      {
        headerName: 'Package',
        cellRenderer: 'agGroupCellRenderer',
        minWidth: 140,
        valueGetter: (p: ValueGetterParams) => {
          const i = p.node?.rowIndex ?? 0;
          return p.data.package_name || `${i + 1} of ${this.packages.length}`;
        }
      },
      {
        headerName: 'Dims',
        minWidth: 110,
        valueGetter: (p: ValueGetterParams) => {
          const { length, width, height, measurement_unit } = p.data;
          return `${length}×${width}×${height} ${this.labels(undefined, measurement_unit).d}`;
        }
      },
      {
        headerName: 'Weight',
        minWidth: 90,
        valueGetter: (p: ValueGetterParams) => {
          const { weight, weight_unit } = p.data;
          return `${weight} ${this.labels(weight_unit, undefined).w}`;
        }
      },
      { headerName: 'Product Count', minWidth: 70, valueGetter: p => p.data.products.length }
    ];

    // Detail grid fields (hide delivered_method)
    const fieldMap: Record<string,{name:string;min?:number}> = {
      barcode:               { name:'Barcode',  min:150 },
      store_number:          { name:'Store',    min: 80 },
      received_in:           { name:'Received', min: 80 },
      audited:               { name:'Audited',  min: 70 },
      delivered:             { name:'Delivered',min: 80 },
      delivered_method:      { name:'Method',   min:110 },
      packaging_type:        { name:'Pkg Type', min: 100 },
      return_status:         { name:'Return',   min:200 },
      carton_resolution:     { name:'Resolution',min:200 },
      product_type:          { name:'Product Type', min:100 },
      product_serial_number: { name:'Serial',   min:130 },
      product_code:          { name:'Code',     min:120 },
      product_model_number:  { name:'Model',    min:110 },
      width:                 { name:'W',        min: 60 },
      height:                { name:'H',        min: 60 },
      length:                { name:'L',        min: 60 },
      weight:                { name:'Wt',       min: 60 },
      condition:             { name:'Condition',min: 70 }
    };
    const hiddenCols = new Set(['delivered_method', 'product_serial_number', 'product_type', 'product_code', 'product_model_number', 'condition' ]);

    //  define select options as strings
    const packagingTypeOptions = ['','1','2','3','4','5'];
    const packagingTypeLabels: Record<string,string> = {
      '':  '',
      '1': 'Palleted',
      '2': 'Carton',
      '3': 'Crate',
      '4': 'Loose / Floor',
      '5': 'Bin'
    };
    const conditionOptions = ['','1','2','3','4'];
    const conditionLabels: Record<string,string> = {
      '':  '',
      '1': 'New',
      '2': 'Used',
      '3': 'Damaged',
      '4': 'Open Box'
    };
    const returnStatusOptions     = [
      '', 'DAMAGED','REALLOCATED','OVERAGE',
      'OUT OF MARKET','OPENED','MISSING',
      'HELD FOR DISPOSITION','MANUAL AUDIT'
    ];
    const cartonResolutionOptions = [
      '', 'BARCODE DAMAGED','DELIVERED AS IS','RETAPED AND DELIVERED',
      'BARCODE ILLEGIBLE','LABEL MISSING','DISCARDED DISPOSED',
      'OUT OF MARKET','HELD FOR DISPOSITION','REPACKAGED AND DELIVERED'
    ];
    const booleanFields = new Set(['received_in','audited','delivered']);

    const detailCols: ColDef[] = Object.entries(fieldMap)
      .filter(([f]) => !hiddenCols.has(f))
      .map(([field, cfg]) => {
        const isEditable = [
          'barcode','store_number',
          'received_in','audited','delivered',
          'packaging_type','condition',
          'return_status','carton_resolution',
          'width','height','length','weight'
        ].includes(field);

        const col: ColDef = {
          field,
          headerName: cfg.name,
          minWidth: cfg.min,
          flex: 1,
          editable: isEditable,
          floatingFilter: !booleanFields.has(field)
        };

        if (field === 'barcode') {
          col.cellClass = 'vip-barcode';
          col.pinned    = 'left';
        }
        if (field === 'return_status' || field === 'carton_resolution') {
          const pref = field.startsWith('return') ? 'rs' : 'cr';
          col.cellClassRules = { [`${pref}-${field.toUpperCase()}`]: p => !!p.value };
        }
        if (booleanFields.has(field)) {
          col.filter       = 'agSetColumnFilter';
          col.cellEditor   = 'agCheckboxCellEditor';
          col.cellRenderer = 'agCheckboxCellRenderer';
        } else if (field === 'packaging_type') {
          col.cellEditor       = 'agSelectCellEditor';
          col.cellEditorParams = { values: packagingTypeOptions };
          col.valueFormatter   = params =>
            packagingTypeLabels[String(params.value)] || '';
          col.valueParser      = params => {
            if (params.newValue === '') return null;
            const n = parseFloat(params.newValue);
            return isNaN(n) ? null : n;
          };
        } else if (field === 'condition') {
          col.cellEditor       = 'agSelectCellEditor';
          col.cellEditorParams = { values: conditionOptions };
          col.valueFormatter   = params =>
            conditionLabels[String(params.value)] || '';
          col.valueParser      = params => {
            if (params.newValue === '') return null;
            const n = parseFloat(params.newValue);
            return isNaN(n) ? null : n;
          };
        } else if (field === 'return_status') {
          col.cellEditor       = 'agSelectCellEditor';
          col.cellEditorParams = { values: returnStatusOptions };
        } else if (field === 'carton_resolution') {
          col.cellEditor       = 'agSelectCellEditor';
          col.cellEditorParams = { values: cartonResolutionOptions };
        }

        return col;
      });

    this.detailCellRendererParams = {
      detailGridOptions: {
        defaultColDef: {
          resizable:      true,
          sortable:       true,
          filter:         true,
          floatingFilter: true,
          cellClass:      'text-ellipsis'
        },
        columnDefs:   detailCols,
        headerHeight: 32,
        rowHeight:    28,
        onGridReady:  (params: GridReadyEvent) => {
          const api    = params.api;
          this.detailGridApi       = api;          // restore column state & filters
          const saved = localStorage.getItem(this.storageKey);
          if (saved) {
            try {
              const { columnState, filterModel } = JSON.parse(saved);
              api.applyColumnState({ state: columnState, applyOrder: true });
              api.setFilterModel(filterModel);
            } catch {}
          }

          // persist on changes
          const saveState = () => {
            const s = {
              columnState: api.getColumnState(),
              filterModel: api.getFilterModel()
            };
            localStorage.setItem(this.storageKey, JSON.stringify(s));
          };
          (['columnMoved','columnResized','columnVisible','sortChanged','filterChanged'] as const)
            .forEach(evt => api.addEventListener(evt, saveState));

          // auto-size all columns
          api.autoSizeAllColumns();

          // handle edits
          api.addEventListener('cellValueChanged', (e: CellValueChangedEvent) => {
            if (!e.colDef.editable || e.oldValue === e.newValue) return;
            let nv = e.newValue;
            if (nv === '') nv = null;
            if (
              ['packaging_type','condition','width','height','length','weight']
                .includes(e.colDef.field!)
              && typeof nv === 'string'
            ) {
              nv = parseFloat(nv);
            }
            this.freightSvc
              .updateProduct(e.data.product_uuid!, { [e.colDef.field!]: nv })
              .subscribe({
                next: () => {},
                error: () => e.node.setDataValue(e.colDef.field!, e.oldValue)
              });
          });
        }
      },
      getDetailRowData: (p: any) => p.successCallback(p.data.products)
    };
  }
}
